import type { ScheduleSpecDto } from '../services/scheduled-jobs.types';
import {
  SCHEDULE_DISPLAY_TIME_ZONE,
  convertTimeOfDay,
  datetimeLocalToIso,
  displayLocale,
  endOfLocalDateIso,
  isoToDateInput,
  isoToDatetimeLocal,
  translate,
} from './scheduled-job-time';

/**
 * Translates the edit modal's Frequency/Time/End-Repeat UI into a backend
 * `ScheduleSpecDto`.
 */
export type ScheduledJobFrequency =
  | 'once'
  | 'hourly'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly'
  | 'custom';

export type ScheduledJobEndRepeat = 'never' | '7d' | '30d' | 'onDate';

export type ScheduledJobBuilderState = {
  frequency: ScheduledJobFrequency;
  timeOfDay: string; // "HH:mm"
  weekdays: number[]; // 0=Sun..6=Sat
  dayOfMonth: number;
  runAt: string; // datetime-local value
  customCron: string;
  endRepeat: ScheduledJobEndRepeat;
  endDate: string; // date value, used when endRepeat === 'onDate'
};

export const DEFAULT_SCHEDULED_JOB_BUILDER_STATE: ScheduledJobBuilderState = {
  frequency: 'daily',
  timeOfDay: '09:00',
  weekdays: [1, 2, 3, 4, 5],
  dayOfMonth: 1,
  runAt: '',
  customCron: '',
  endRepeat: 'never',
  endDate: '',
};

function parseTimeOfDay(value: string): { hour: number; minute: number } | null {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return null;
  const [hh, mm] = value.split(':');
  const hour = parseInt(hh, 10);
  const minute = parseInt(mm, 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return { hour, minute };
}

function buildCron(state: ScheduledJobBuilderState): string | null {
  switch (state.frequency) {
    case 'hourly':
      return '0 * * * *';
    case 'weekdays': {
      const parsed = parseTimeOfDay(state.timeOfDay);
      if (!parsed) return null;
      return `${parsed.minute} ${parsed.hour} * * 1-5`;
    }
    case 'daily': {
      const parsed = parseTimeOfDay(state.timeOfDay);
      if (!parsed) return null;
      return `${parsed.minute} ${parsed.hour} * * *`;
    }
    case 'weekly': {
      const parsed = parseTimeOfDay(state.timeOfDay);
      if (!parsed || state.weekdays.length === 0) return null;
      const days = [...state.weekdays].sort((a, b) => a - b).join(',');
      return `${parsed.minute} ${parsed.hour} * * ${days}`;
    }
    case 'monthly': {
      const parsed = parseTimeOfDay(state.timeOfDay);
      const dom = Math.floor(state.dayOfMonth);
      if (!parsed || !Number.isFinite(dom) || dom < 1 || dom > 31) return null;
      return `${parsed.minute} ${parsed.hour} ${dom} * *`;
    }
    case 'custom':
      return state.customCron.trim() || null;
    case 'once':
      return null;
  }
}

function buildEndAt(state: ScheduledJobBuilderState, timezone: string): string | undefined {
  switch (state.endRepeat) {
    case 'never':
      return undefined;
    case '7d':
      return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    case '30d':
      return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'onDate':
      return state.endDate ? endOfLocalDateIso(state.endDate, timezone) : undefined;
  }
}

/** Returns `null` when the current state can't produce a valid schedule yet. */
export function buildScheduleSpec(
  state: ScheduledJobBuilderState,
  timezone: string = SCHEDULE_DISPLAY_TIME_ZONE
): ScheduleSpecDto | null {
  if (state.frequency === 'once') {
    const trimmed = state.runAt.trim();
    if (!trimmed) return null;
    const runAt = datetimeLocalToIso(trimmed, timezone) || trimmed;
    return { scheduleKind: 'once', runAt, timezone };
  }

  const cron = buildCron(state);
  if (!cron) return null;
  return { scheduleKind: 'cron', cron, timezone, endAt: buildEndAt(state, timezone) };
}

const FREQUENCY_LABELS: Record<ScheduledJobFrequency, { key: string; defaultValue: string }> = {
  once: { key: 'ONCE', defaultValue: 'Once' },
  hourly: { key: 'EVERY_HOUR', defaultValue: 'Every hour' },
  daily: { key: 'DAILY', defaultValue: 'Daily' },
  weekdays: { key: 'WEEKDAYS', defaultValue: 'Weekdays' },
  weekly: { key: 'WEEKLY', defaultValue: 'Weekly' },
  monthly: { key: 'MONTHLY', defaultValue: 'Monthly' },
  custom: { key: 'CUSTOM', defaultValue: 'Custom' },
};

const frequencyLabel = (frequency: ScheduledJobFrequency) =>
  translate(FREQUENCY_LABELS[frequency].key, FREQUENCY_LABELS[frequency].defaultValue);

/** Short label shown before a live preview response arrives. */
export function describeScheduleState(state: ScheduledJobBuilderState): string {
  return frequencyLabel(state.frequency);
}

/** One-line schedule for a routine row, e.g. "Weekdays at 09:00 (Bangladesh Standard Time)". */
export function describeScheduleSpec(
  spec: ScheduleSpecDto,
  displayTimeZone: string = SCHEDULE_DISPLAY_TIME_ZONE
): string {
  const state = builderStateFromSpec(spec, displayTimeZone);
  const label = frequencyLabel(state.frequency);
  // Once, hourly and custom have no single time of day to show.
  if (!['daily', 'weekdays', 'weekly', 'monthly'].includes(state.frequency)) return label;

  const zoneName = new Intl.DateTimeFormat(displayLocale(), {
    timeZone: displayTimeZone,
    timeZoneName: 'long',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
  return translate('SCHEDULE_FREQUENCY_AT', '{{frequency}} at {{time}} ({{zone}})', {
    frequency: label,
    time: state.timeOfDay,
    zone: zoneName ?? '',
  });
}

/** Seeds builder state from a job's existing `ScheduleSpecDto` (edit-open time). */
export function builderStateFromSpec(
  spec: ScheduleSpecDto | null | undefined,
  displayTimeZone: string = SCHEDULE_DISPLAY_TIME_ZONE
): ScheduledJobBuilderState {
  if (!spec) return DEFAULT_SCHEDULED_JOB_BUILDER_STATE;

  if (spec.scheduleKind === 'once') {
    const runAt = spec.runAt ? isoToDatetimeLocal(spec.runAt, displayTimeZone) : '';
    return { ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE, frequency: 'once', runAt };
  }

  const cron = (spec.cron || '').trim();
  const parts = cron.split(/\s+/);
  const sourceTimeZone = spec.timezone || 'UTC';
  const endRepeat: ScheduledJobEndRepeat = spec.endAt ? 'onDate' : 'never';
  const endDate = spec.endAt ? isoToDateInput(spec.endAt, displayTimeZone) : '';

  if (parts.length === 5) {
    const [minute, hour, dom, , dow] = parts;
    const sourceTimeOfDay =
      /^\d+$/.test(minute) && /^\d+$/.test(hour)
        ? `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
        : DEFAULT_SCHEDULED_JOB_BUILDER_STATE.timeOfDay;
    const timeOfDay = convertTimeOfDay(sourceTimeOfDay, sourceTimeZone, displayTimeZone);

    if (minute === '0' && hour === '*' && dom === '*' && dow === '*') {
      return { ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE, frequency: 'hourly', endRepeat, endDate };
    }
    if (dow === '1-5' && dom === '*') {
      return { ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE, frequency: 'weekdays', timeOfDay, endRepeat, endDate };
    }
    if (dom === '*' && dow !== '*') {
      const weekdays = dow
        .split(',')
        .map((d) => parseInt(d, 10))
        .filter((d) => Number.isFinite(d));
      return {
        ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE,
        frequency: 'weekly',
        timeOfDay,
        weekdays: weekdays.length ? weekdays : DEFAULT_SCHEDULED_JOB_BUILDER_STATE.weekdays,
        endRepeat,
        endDate,
      };
    }
    if (dom !== '*' && dow === '*') {
      const parsedDom = parseInt(dom, 10);
      return {
        ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE,
        frequency: 'monthly',
        timeOfDay,
        dayOfMonth: Number.isFinite(parsedDom) ? parsedDom : 1,
        endRepeat,
        endDate,
      };
    }
    if (dom === '*' && dow === '*' && /^\d+$/.test(minute) && /^\d+$/.test(hour)) {
      return { ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE, frequency: 'daily', timeOfDay, endRepeat, endDate };
    }
  }

  return { ...DEFAULT_SCHEDULED_JOB_BUILDER_STATE, frequency: 'custom', customCron: cron, endRepeat, endDate };
}
